import { api } from "../api";

export interface ImportPreview {
  jobId: string;
  rows: ImportPreviewRow[];
  summary: {
    total: number;
    found: number;
    notFound: number;
    needsReview: number;
    errors: number;
  };
}

export interface ImportPreviewRow {
  rowIndex: number;
  isbn: string;
  copies: number;
  locationCode?: string;
  collectionCode?: string;
  conditionCode?: string;
  itemTypeCode?: string;
  accessionNumber?: number;
  bindingType?: string; // "hbk" or "pbk"
  lcClassification?: string;
  callNumber?: string;
  statusCode?: string;
  subjects?: string;
  acquisitionMode?: string;
  status: "FOUND" | "NOT_FOUND" | "NEEDS_REVIEW";
  existingBiblioId?: string | null;
  existingCopyCount?: number;
  metadata?: {
    title?: string;
    authors?: string[];
    publisher?: string;
    publishedDate?: string;
    lcClassification?: string;
    callNumber?: string;
    subjects?: string[];
  };
  errors?: string[];
}

export interface ImportReport {
  jobId: string;
  status: string;
  summary: {
    rows: number;
    success: number;
    failed: number;
  };
  reportData?: any;
  errorMessage?: string;
  createdAt: string;
  updatedAt: string;
}

export const importsApi = {
  /**
   * Upload CSV file for bulk import
   */
  upload: async (
    file: File,
    defaults?: {
      defaultLocationCode?: string;
      defaultCollectionCode?: string;
      defaultConditionCode?: string;
    }
  ): Promise<{ jobId: string }> => {
    const formData = new FormData();
    formData.append("file", file);
    if (defaults?.defaultLocationCode) {
      formData.append("defaultLocationCode", defaults.defaultLocationCode);
    }
    if (defaults?.defaultCollectionCode) {
      formData.append("defaultCollectionCode", defaults.defaultCollectionCode);
    }
    if (defaults?.defaultConditionCode) {
      formData.append("defaultConditionCode", defaults.defaultConditionCode);
    }

    const response = await api.post("/imports/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  /**
   * Get import preview
   */
  getPreview: async (jobId: string): Promise<ImportPreview> => {
    const response = await api.get(`/imports/${jobId}/preview`);
    return response.data;
  },

  /**
   * Confirm and start import
   */
  confirm: async (jobId: string, approvedRows?: number[]): Promise<{ success: boolean }> => {
    const response = await api.post(`/imports/${jobId}/confirm`, { approvedRows });
    return response.data;
  },

  /**
   * Get import job status
   */
  getJobStatus: async (
    jobId: string
  ): Promise<{
    id: string;
    status: string;
    summary?: any;
    errorMessage?: string;
    createdAt: string;
    updatedAt: string;
  }> => {
    const response = await api.get(`/imports/${jobId}/status`);
    return response.data;
  },

  /**
   * Get import report
   */
  getReport: async (jobId: string): Promise<ImportReport> => {
    const response = await api.get(`/imports/${jobId}/report`);
    return response.data;
  },
};
